import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { QuarryData } from "../Types";
import { appRoutes } from "../constants/appRoutes";
import { useLocation } from "react-router-dom";

// Define the shape of a material object
export interface Material {
  key: string;
  vendor: string;
  vendorId: string;
  materialName: string;
  materialSelected: string;
  landedPrice: number;
  material?: string;
  updatedTopQuarriesInfo?: QuarryData;
}

export interface MaterialTruck {
  uniqKey?: string;
  type: string;
  truckType: string;
  materialId: string;
  id: string;
  label: string;
  value: string;
  category: string;
  categoryKey: string;
  subShortName: string;
  materialTax: string;
  material?: string;
  additionalTaxRate: number;
  orginalTaxRate: number;
  materialShortValue: string;
  vendor: string;
  vendorId: string;
  quarryNickName: string;
  vendorTerms: string;
  vendorDiscountTerms: string;
  pickUpAddress: string;
  pickUpLatLng: {
    lat: number;
    lng: number;
  };
  materialCostAtQuarryPerTon: number;
  duration: string;
  milesData: string;
  landedPrice: number;
  landedPriceType: string;
  landedCost: number;
  landedCostType: string;
  totalSale: number;
  totalExepense: number;
  totalProfit: number;
  totalProfitPercentage: number;
  diameter: boolean;
  materialName: string;
  materialSelected?: string;
  materialQuantity: number;
  materialQuantityUnitType: string;
  truckingQuantity: number;
  truckingQuantityUnitType: string;
  minimumHours: number;
  materialCost: number;
  materialPrice: number;
  truckingCost: number;
  truckingPrice: number;
  landedPrice: number;
  useMaterialMarkup: number;
  materialCostType: string;
  materialPriceType: string;
  truckingCostType: string;
  truckingPriceType: string;
  minimumHours: number;
}
// Define the context type
interface MaterialsContextType {
  materialsList: Material;
  addMaterial: (material: Material) => void;
  materialTruckList: Material[];
  addTruckMaterial: (material: MaterialTruck) => void;
  updateMaterial: (id: number, updatedMaterial: Partial<Material>) => void;
  removeMaterial: (id: number) => void;
  resetTruckMaterial: () => void;
  removeTruckMaterial: (id: number) => void;
  getTruckMaterial: (uniqKey: string) => MaterialTruck;
}

// Create the context with an initial undefined value
const MaterialsContext = createContext<MaterialsContextType | undefined>(
  undefined
);

// Provider component
export const MaterialsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [materialsList, setMaterialsList] = useState<Material>();
  const [materialTruckList, setMaterialTruckList] = useState<MaterialTruck[]>(
    []
  );

  // Function to add a new material
  const addMaterial = (material: Material) => {
    setMaterialsList(material);
  };

  const addTruckMaterial = (material: Material) => {
    setMaterialTruckList((prev) => [...prev, material]);
  };

  const resetTruckMaterial = () => {
    setMaterialTruckList([]);
  };
  const getTruckMaterial = (uniqKey: number): MaterialTruck => {
    return materialTruckList.find((material) => material.uniqKey === uniqKey);
  };

  const compareTrucks = (truck1: MaterialTruck, truck2: any): boolean => {
    // Helper function to compare arrays regardless of order
    const compareTruckTypes = (a: string[], b: string[]): boolean => {
      if (a.length !== b.length) return false;
      const setA = new Set(a);
      const setB = new Set(b);
      if (setA.size !== setB.size) return false;
      for (const item of setA) if (!setB.has(item)) return false;
      return true;
    };

    // Compare primitive properties with proper type handling
    if (
      String(truck1.materialCost) !== String(truck2.materialCost) ||
      truck1.materialCostType !== truck2.materialCostType ||
      truck1.materialName !== truck2.materialName ||
      String(truck1.materialPrice) !== String(truck2.materialPrice) ||
      truck1.materialPriceType !== truck2.materialPriceType ||
      String(truck1.materialQuantity) !== String(truck2.materialQuantity) ||
      truck1.materialQuantityUnitType !== truck2.materialQuantityUnitType ||
      !compareTruckTypes(truck1.truckType, truck2.truckType) ||
      String(truck1.truckingCost) !== String(truck2.truckingCost) ||
      String(truck1.truckingPrice) !== String(truck2.truckingPrice) ||
      truck1.truckingCostType !== truck2.truckingCostType ||
      truck1.truckingPriceType !== truck2.truckingPriceType ||
      truck1.vendor !== truck2.vendorName
    ) {
      return false;
    }
    return true;
  };
  const removeTruckMaterial = (row?: any) => {
    setMaterialTruckList((prev) =>
      prev.filter((truck) => !compareTrucks(truck, row))
    );
  };

  // Function to update an existing material
  const updateMaterial = (id: number, updatedMaterial: Partial<Material>) => {
    setMaterialTruckList(
      materialTruckList?.map((material) =>
        material?.uniqKey === id
          ? { ...material, ...updatedMaterial }
          : material
      )
    );
  };

  // Function to remove a material by ID
  const removeMaterial = (id: number) => {
    setMaterialsList((prev) => prev.filter((material) => material.id !== id));
  };

  const location = useLocation();

  useEffect(() => {
    if (
      location &&
      !location?.pathname?.startsWith(appRoutes.ADD_TRUCKING_MATERIAL) &&
      !location?.pathname?.startsWith(appRoutes.UPDATE_TRUCKING_MATERIAL) &&
      !location?.pathname?.startsWith(appRoutes.VIEW_TRUCKING_MATERIAL)
    ) {
      resetTruckMaterial();
    }
  }, [location?.pathname]);

  return (
    <MaterialsContext.Provider
      value={{
        materialsList,
        addMaterial,
        updateMaterial,
        removeMaterial,
        addTruckMaterial,
        removeTruckMaterial,
        getTruckMaterial,
        materialTruckList,
        resetTruckMaterial,
      }}
    >
      {children}
    </MaterialsContext.Provider>
  );
};

// Custom hook for easy access to the context
export const useMaterials = (): MaterialsContextType => {
  const context = useContext(MaterialsContext);
  if (!context) {
    throw new Error("useMaterials must be used within a MaterialsProvider");
  }
  return context;
};
